/**
 * The two German sentences that describe a field the CALCULATION RUN owns but has not filled in.
 *
 * <h2>Why this file exists</h2>
 * Two surfaces render these sentences: the top-down provision wizards
 * ({@code cp-provision-contract-create-wizard}, {@code cp-provision-contract-adjust-wizard}) and the
 * detail views of the provision object components. The acceptance test asserts the wording, so if the
 * two ever drift apart it silently checks two different things and one of the surfaces stops being
 * covered. Holding the strings once makes the COMPILER guarantee the match, instead of relying on two
 * authors remembering the same sentence.
 *
 * <p>Placed in {@code service/domain/} on purpose: both consumers may import downwards from a
 * component into the module's domain layer, while the reverse — a service reaching up into an object
 * component for a constant — would invert the layering.
 *
 * <h2>Why the field text carries no time reference</h2>
 * An empty run-owned field is not one situation but THREE, and only the first is "the run has not
 * been here yet":
 *
 * <ol>
 *   <li><b>The run has not processed this row yet</b> — the normal case.</li>
 *   <li><b>The row is never selected by the run.</b> A hand-made Provisionszeile only enters the
 *       calculation if the selection catches it; miss every branch and the fields stay empty
 *       indefinitely. The wizards warn about this case at capture time.</li>
 *   <li><b>The run ran and found nothing applicable</b> — no matrix resolved, the fields written
 *       explicitly to null. The run has already been here.</li>
 * </ol>
 *
 * "Wird mit dem nächsten Lauf berechnet" holds only in state 1. Even "noch nicht berechnet" carries a
 * faint promise of a run still to come — which state 2 never keeps and state 3 has already broken.
 * {@link CP_PROVISION_PENDING_FIELD_TEXT} therefore states the present and nothing else, and is true in all
 * three.
 *
 * <p>The expectation for the NORMAL case is not lost, it is relocated: {@link CP_PROVISION_PENDING_BLOCK_TEXT}
 * says it ONCE at the head of a block, where it reads as a general statement about how things usually
 * go rather than as a guarantee attached to every single field.
 *
 * <p>A field that DID receive a value shows the value. These sentences are for the empty case only,
 * and they must never appear over a value the user entered themselves — that would present their own
 * input as provisional.
 */

/** Per FIELD. Present tense, no promise about the future — see the file header. */
export const CP_PROVISION_PENDING_FIELD_TEXT: string = 'nicht berechnet';

/** Per BLOCK, stated once at its head. Carries the expectation for the normal case. */
export const CP_PROVISION_PENDING_BLOCK_TEXT: string = 'Diese Werte ergänzt der nächste Berechnungslauf.';
